// Heatmap Module Helper Functions
export const CANVAS_W = 840;
export const CANVAS_H = 476; // ~16:9

// Responsive Heatmap Sizing Constants
export const CANVAS_MAX_W_DEFAULT = 760;
export const CANVAS_MAX_W_XL = 880;
export const CANVAS_MIN_H = 280;
export const CANVAS_MAX_H = 520;
export const CANVAS_VIEWPORT_OFFSET_H = 280;

export const BADGE_COLORS = {
  XLT_ZONE_OCCUPANCY_COUNT: { solid: 'rgba(255, 110, 64, 1)', half: 'rgba(255, 110, 64, 0.50)', hex: '#FF6E40' },
  GENERAL: { solid: 'rgba(255, 110, 64, 1)', half: 'rgba(255, 110, 64, 0.50)', hex: '#FF6E40' },
  XLT_ZONE_IN_OUT_COUNT: { solid: 'rgba(171, 71, 188, 1)', half: 'rgba(171, 71, 188, 0.50)', hex: '#AB47BC' },
  XLT_LINE_IN_OUT_COUNT: { solid: 'rgba(0, 229, 255, 1)', half: 'rgba(0, 229, 255, 0.50)', hex: '#00E5FF' },
  XLT_GROUP_LINE_IN_OUT_COUNT: { solid: 'rgba(38, 198, 218, 1)', half: 'rgba(38, 198, 218, 0.50)', hex: '#26C6DA' },
  XLT_ATTENTION: { solid: 'rgba(255, 213, 79, 1)', half: 'rgba(255, 213, 79, 0.50)', hex: '#FFD54F' },
  XLT_QUEUE_STATISTICS: { solid: 'rgba(129, 199, 132, 1)', half: 'rgba(129, 199, 132, 0.50)', hex: '#81C784' },
};

export const drawArrowhead = (ctx, x0, y0, x1, y1, dpr) => {
  const angle = Math.atan2(y1 - y0, x1 - x0);
  const len = 9 * dpr;
  ctx.save();
  ctx.setLineDash([]);

  // 1. Arrowhead pointing forward along the line (away from x0)
  const tx1 = x0 + len * Math.cos(angle);
  const ty1 = y0 + len * Math.sin(angle);

  ctx.beginPath();
  ctx.moveTo(tx1, ty1);
  ctx.lineTo(tx1 - len * Math.cos(angle - Math.PI / 6), ty1 - len * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(tx1 - len * Math.cos(angle + Math.PI / 6), ty1 - len * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fillStyle = ctx.strokeStyle;
  ctx.fill();

  // 2. Arrowhead pointing backward along the line (away from x0 in opposite direction)
  const tx2 = x0 - len * Math.cos(angle);
  const ty2 = y0 - len * Math.sin(angle);

  ctx.beginPath();
  ctx.moveTo(tx2, ty2);
  ctx.lineTo(tx2 + len * Math.cos(angle - Math.PI / 6), ty2 + len * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(tx2 + len * Math.cos(angle + Math.PI / 6), ty2 + len * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fillStyle = ctx.strokeStyle;
  ctx.fill();

  ctx.restore();
};

export const formatDwellTime = (seconds) => {
  if (seconds === null || seconds === undefined || isNaN(seconds)) return null;
  const totalSecs = Math.round(seconds);
  if (totalSecs < 60) return `${totalSecs}s`;
  const minutes = Math.floor(totalSecs / 60);
  const remainingSecs = totalSecs % 60;
  return remainingSecs > 0 ? `${minutes}m ${remainingSecs}s` : `${minutes}m`;
};

export const getAgeLabelsAndValues = (zone) => {
  if (!zone) return [];

  let thresholds = [16, 40, 65];
  if (zone.ageThresholdsJson) {
    try {
      const parsed = typeof zone.ageThresholdsJson === 'string' ? JSON.parse(zone.ageThresholdsJson) : zone.ageThresholdsJson;
      if (Array.isArray(parsed) && parsed.length === 3) {
        thresholds = parsed;
      }
    } catch (e) {
      thresholds = [16, 40, 65];
    }
  }

  const childVal = zone.childCount ?? zone.ageBin0 ?? 0;
  const adultVal = zone.adultCount ?? zone.ageBin1 ?? 0;
  const elderVal = zone.elderCount ?? zone.ageBin2 ?? 0;
  const bin3Val = zone.ageBin3 ?? 0;

  const bins = [childVal, adultVal, elderVal, bin3Val];
  const labels = [
    `< ${thresholds[0]}`,
    `${thresholds[0]} - ${thresholds[1]}`,
    `${thresholds[1]} - ${thresholds[2]}`,
    `> ${thresholds[2]}`
  ];

  return bins.map((val, idx) => ({
    label: labels[idx] || `Bin ${idx}`,
    value: typeof val === 'number' && !isNaN(val) ? val : 0
  }));
};

export const pointInPolygon = (px, py, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};

export const pointToSegmentDist = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
};

export const hitTestZones = (mx, my, zoneRegions) => {
  for (let i = zoneRegions.length - 1; i >= 0; i--) {
    const zr = zoneRegions[i];
    if (zr.isZone && zr.polygon.length >= 3) {
      if (pointInPolygon(mx, my, zr.polygon)) return zr.index;
    } else if (zr.segments) {
      for (const seg of zr.segments) {
        if (pointToSegmentDist(mx, my, seg.x1, seg.y1, seg.x2, seg.y2) < 10) return zr.index;
      }
    }
    // Also check badge circle hit
    const bdx = mx - zr.badgeCx;
    const bdy = my - zr.badgeCy;
    if (bdx * bdx + bdy * bdy <= (zr.badgeR + 4) * (zr.badgeR + 4)) return zr.index;
  }
  return -1;
};
